import { FC, useMemo } from 'react';

import { useParams } from 'react-router';

import { Container, Stack, Typography } from '@mui/material';

import Meta from '@/components/Meta';
import NotFound from '@/components/NotFound';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin';
import useOrg from '@/hooks/useOrg';
import GlobalHeader from '@/sections/GlobalHeader';
import Sidebar from '@/sections/Sidebar';
import asyncComponentLoader from '@/utils/react/loader';
import { DialogsProvider } from '@toolpad/core/useDialogs';

import Header from './Header';
import { MatchParams, Tabs } from './types';

function OrgSettings() {
  const { tabId } = useParams<MatchParams>();
  const org = useOrg();
  const isOrgAdmin = useIsOrgAdmin();
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();

  const ContentComponents: Record<Tabs, FC> = useMemo(
    () => ({
      // this is the same
      [Tabs.General]: asyncComponentLoader(() => import('@/pages/Settings/Tabs/General')),
      [Tabs.Members]: asyncComponentLoader(() => import('./Members')),
      // this is the same
      [Tabs.Billing]: isOrgAdmin
        ? asyncComponentLoader(() => import('@/pages/Settings/Tabs/Billing'))
        : () => <NotFound sx={{ background: 'transparent' }} />,
      // this is the same
      [Tabs.Activity]: isOrgAdmin
        ? asyncComponentLoader(() => import('@/sections/Workspace/ActivityFeed'), {
            created: org.created,
          })
        : () => <NotFound sx={{ background: 'transparent' }} />,
      [Tabs.ApiKeys]: asyncComponentLoader(() => import('@/sections/APIKeys')),
      [Tabs.Environments]: !isMultipleTeamsEnabled
        ? asyncComponentLoader(() => import('@/pages/TeamSettings/Environments'))
        : () => <NotFound sx={{ background: 'transparent' }} />,
      [Tabs.Secrets]: !isMultipleTeamsEnabled
        ? asyncComponentLoader(() => import('@/pages/Settings/Tabs/Secrets'))
        : () => <NotFound sx={{ background: 'transparent' }} />,
    }),
    [org.created, isOrgAdmin, isMultipleTeamsEnabled],
  );

  function getContent() {
    const ContentComponent = ContentComponents[tabId || Tabs.General];

    return (
      <Stack
        sx={{
          overflow: 'auto',
          height: '100%',
          backgroundColor: (theme) => theme.palette.background.subtle,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
        key={tabId}
      >
        <Stack
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            position: 'relative',
            flexGrow: 1,
          }}
        >
          <Container
            maxWidth="lg"
            sx={{ py: 4, pb: tabId === Tabs.Activity ? 0 : 4, height: '100%' }}
          >
            <ContentComponent />
          </Container>
        </Stack>
      </Stack>
    );
  }

  return (
    <Meta title="Organization settings">
      <DialogsProvider>
        <Stack direction="row" height="100%" width="100%" overflow="hidden">
          <Sidebar />
          <Stack
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'auto',
            }}
          >
            <GlobalHeader
              title={
                <Typography variant="h5" color="text.default">
                  Organization settings
                </Typography>
              }
              sx={{
                pl: 4,
              }}
            />
            <Header />
            {getContent()}
          </Stack>
        </Stack>
      </DialogsProvider>
    </Meta>
  );
}

export default OrgSettings;
